/**
 * GridCrash — Player Profile Sync
 *
 * The server-side mirror of local progress. localStorage remains the
 * source of truth for offline play; the profiles row (keyed to the
 * anonymous auth user) makes coins/unlocks/themes survive across
 * devices-clearing events and powers the cosmetic economy.
 *
 * Currency columns (clash_coins, lifetime_score, unlocked_assets) are
 * NOT writable by the client — they only move through SECURITY DEFINER
 * RPCs (migrate_local_progress, migrate_local_wallet, award_game_rewards,
 * purchase_asset, purchase_deal_asset). Equipped cosmetics are directly
 * writable (column-level grants).
 */

import { getClient, ensureSession } from "./auth.js";
import { getLifetimeScore } from "./unlocks.js";
import { getAchievementState } from "./achievements.js";
import { getWallet, markMigrated, setEquippedLocal, spendLocal } from "./wallet.js";

const MIGRATED_KEY = "gridcrash.profile.migrated";
const CACHE_KEY = "gridcrash.profile.cache";

export interface Profile {
  id: string;
  lifetime_score: number;
  clash_coins: number;
  unlocked_assets: string[];
  equipped_theme: string;
  equipped_animation: string;
}

let profile: Profile | null = null;

// ─── Cache ────────────────────────────────────────────────────────────────────

/** In-memory profile, falling back to the last-synced localStorage copy. */
export function getCachedProfile(): Profile | null {
  if (profile) return profile;
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) profile = JSON.parse(raw) as Profile;
  } catch { /* corrupt cache — ignore */ }
  return profile;
}

function setCache(p: Profile): void {
  profile = p;
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(p)); } catch { /* ignore */ }
}

// ─── Init & Migration ─────────────────────────────────────────────────────────

/**
 * Establishes the anonymous session, creates the profile row on first
 * run, and performs the one-time localStorage → server migration of
 * lifetime score + achievements. Safe to call fire-and-forget on boot.
 */
export async function initProfile(): Promise<Profile | null> {
  const userId = await ensureSession();
  const client = await getClient();
  if (!userId || !client) return getCachedProfile();

  try {
    const { data } = await client
      .from("profiles")
      .select("id, lifetime_score, clash_coins, unlocked_assets, equipped_theme, equipped_animation")
      .eq("id", userId)
      .maybeSingle();

    let row = data as Profile | null;

    if (!row) {
      const { data: inserted } = await client
        .from("profiles")
        .insert({ id: userId })
        .select("id, lifetime_score, clash_coins, unlocked_assets, equipped_theme, equipped_animation")
        .single();
      row = inserted as Profile | null;
    }

    if (row && localStorage.getItem(MIGRATED_KEY) !== "true") {
      const { error } = await client.rpc("migrate_local_progress", {
        p_lifetime: getLifetimeScore(),
        p_achievements: getAchievementState().earned,
      });
      if (!error) {
        try { localStorage.setItem(MIGRATED_KEY, "true"); } catch { /* ignore */ }
        row = await refetch(userId) ?? row;
      }
    }

    // Fold offline wallet earnings into the server row. Quest/streak
    // rewards still land in the local wallet only, so this isn't a
    // one-shot — it tops up clash_coins (clamped server-side) any time
    // there's a local balance to fold in.
    if (row) row = (await syncLocalWallet()) ?? row;

    if (row) setCache(row);
    return row ?? getCachedProfile();
  } catch {
    return getCachedProfile();
  }
}

// ─── Economy ──────────────────────────────────────────────────────────────────

/**
 * Folds any pending local-wallet coins/unlocks into clash_coins. Safe to
 * call any time and repeatedly — quest/streak rewards keep landing in
 * the local wallet (no claim_quest RPC yet), so this is an ongoing
 * top-up, not a one-shot migration. No-ops offline or with nothing to
 * fold. Concurrent callers (boot + store open) share one in-flight fold
 * so the same coins can't be credited twice.
 */
let walletSync: Promise<Profile | null> | null = null;

export function syncLocalWallet(): Promise<Profile | null> {
  walletSync ??= doSyncLocalWallet().finally(() => { walletSync = null; });
  return walletSync;
}

async function doSyncLocalWallet(): Promise<Profile | null> {
  const userId = await ensureSession();
  const client = await getClient();
  if (!userId || !client) return getCachedProfile();

  const w = getWallet();
  if (w.coins <= 0 && w.unlocked.length === 0) return getCachedProfile();

  try {
    const { data, error } = await client.rpc("migrate_local_wallet", {
      p_coins: w.coins,
      p_assets: w.unlocked,
    });
    // Null data means the fold didn't land — the pre-topup void version
    // of the RPC is still deployed, or there's no profile row. Keep the
    // local balance either way.
    if (error || data == null) return getCachedProfile();
    markMigrated(w.coins);
    const row = await refetch(userId);
    if (row) setCache(row);
    return row ?? getCachedProfile();
  } catch {
    return getCachedProfile();
  }
}

/** Reward tier for the server RPC — maps engine difficulty ids. */
export type RewardTier = "easy" | "medium" | "expert" | "daily";

export function rewardTierFor(difficultyId: string, isDaily: boolean): RewardTier {
  if (isDaily) return "daily";
  return difficultyId === "hard" ? "expert" : (difficultyId as RewardTier);
}

export interface RewardResult {
  coinsEarned: number;
  clashCoins: number;
}

/**
 * Credits coins for a finished game: coins = max(1, floor(score/100)),
 * computed server-side against per-tier caps. Null when offline or
 * the server rejects the claim — the game shrugs and moves on.
 */
export async function awardRewards(score: number, tier: RewardTier): Promise<RewardResult | null> {
  const userId = await ensureSession();
  const client = await getClient();
  if (!userId || !client) return null;

  const before = getCachedProfile()?.clash_coins ?? 0;
  try {
    const { data, error } = await client.rpc("award_game_rewards", {
      p_score: score,
      p_tier: tier,
    });
    if (error || !data) return null;
    const row = (Array.isArray(data) ? data[0] : data) as
      { clash_coins: number; lifetime_score: number } | undefined;
    if (!row) return null;

    const current = getCachedProfile();
    if (current) {
      setCache({ ...current, clash_coins: row.clash_coins, lifetime_score: row.lifetime_score });
    }
    return { coinsEarned: Math.max(0, row.clash_coins - before), clashCoins: row.clash_coins };
  } catch {
    return null;
  }
}

export interface StoreItem {
  id: string;
  kind: "theme" | "animation" | "mascot_pack" | "crate";
  label: string;
  price: number;
}

export async function fetchStoreItems(): Promise<StoreItem[]> {
  const client = await getClient();
  if (!client) return [];
  try {
    const { data } = await client
      .from("store_items")
      .select("id, kind, label, price")
      .order("price", { ascending: true });
    return (data as StoreItem[] | null) ?? [];
  } catch {
    return [];
  }
}

/** Buys an item; returns the new coin balance or an error message. */
export async function purchase(itemId: string): Promise<{ ok: true; coins: number } | { ok: false; error: string }> {
  const userId = await ensureSession();
  const client = await getClient();
  if (!userId || !client) return { ok: false, error: "Not connected" };

  try {
    const { data, error } = await client.rpc("purchase_asset", { p_item: itemId });
    if (error) {
      const msg = error.message.includes("insufficient") ? "Not enough coins" : error.message;
      return { ok: false, error: msg };
    }
    const coins = data as number;
    const current = getCachedProfile();
    if (current) {
      setCache({
        ...current,
        clash_coins: coins,
        unlocked_assets: current.unlocked_assets.includes(itemId)
          ? current.unlocked_assets
          : [...current.unlocked_assets, itemId],
      });
    }
    return { ok: true, coins };
  } catch {
    return { ok: false, error: "Purchase failed" };
  }
}

/**
 * Spends coins from the effective balance for sinks with no store row
 * (hints). Local wallet first (offline earnings), then the server
 * balance via spend_coins — folding any local remainder in beforehand
 * so a split balance can't strand an affordable purchase.
 */
export async function spendCoins(amount: number): Promise<boolean> {
  if (amount <= 0) return true;
  if (spendLocal(amount)) return true;

  const userId = await ensureSession();
  const client = await getClient();
  if (!userId || !client) return false;

  try {
    if (getWallet().coins > 0) await syncLocalWallet();
    const { data, error } = await client.rpc("spend_coins", { p_amount: amount });
    if (error || data == null) return false;
    const coins = data as number;
    const current = getCachedProfile();
    if (current) setCache({ ...current, clash_coins: coins });
    return true;
  } catch {
    return false;
  }
}

/** Buys the featured deal item at its 30%-off price (server-verified). */
export async function purchaseDeal(
  itemId: string,
  price: number
): Promise<{ ok: true; coins: number } | { ok: false; error: string }> {
  const userId = await ensureSession();
  const client = await getClient();
  if (!userId || !client) return { ok: false, error: "Not connected" };

  try {
    const { data, error } = await client.rpc("purchase_deal_asset", { p_item: itemId, p_price: price });
    if (error) {
      const msg = error.message.includes("insufficient") ? "Not enough coins" : error.message;
      return { ok: false, error: msg };
    }
    const coins = data as number;
    const current = getCachedProfile();
    if (current) {
      setCache({
        ...current,
        clash_coins: coins,
        unlocked_assets: current.unlocked_assets.includes(itemId)
          ? current.unlocked_assets
          : [...current.unlocked_assets, itemId],
      });
    }
    return { ok: true, coins };
  } catch {
    return { ok: false, error: "Purchase failed" };
  }
}

/** Equips an owned theme or animation. Mirrors locally for instant UI. */
export async function equip(kind: "theme" | "animation", assetId: string): Promise<boolean> {
  // Wallet copy covers never-online players (no profile cache to mirror into).
  setEquippedLocal(kind, assetId);

  const current = getCachedProfile();
  if (current) {
    setCache({
      ...current,
      ...(kind === "theme" ? { equipped_theme: assetId } : { equipped_animation: assetId }),
    });
  }

  const userId = await ensureSession();
  const client = await getClient();
  if (!userId || !client) return false;

  try {
    const patch = kind === "theme"
      ? { equipped_theme: assetId }
      : { equipped_animation: assetId };
    const { error } = await client.from("profiles").update(patch).eq("id", userId);
    return !error;
  } catch {
    return false;
  }
}

async function refetch(userId: string): Promise<Profile | null> {
  const client = await getClient();
  if (!client) return null;
  const { data } = await client
    .from("profiles")
    .select("id, lifetime_score, clash_coins, unlocked_assets, equipped_theme, equipped_animation")
    .eq("id", userId)
    .maybeSingle();
  return (data as Profile | null) ?? null;
}
